import React, { useState, useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { TranslateContext } from 'core/translate';
import { ModelInfoProvider } from './common/ModelInfoContext';
import { SvgIconProvider } from 'core/icons';
import { translate } from './utils/translate/translations';
import { BrowserRouter } from 'react-router-dom';
import { ThemeContext } from '../core/css/Themed';
import NotificationsProvider from './common/notifications/NotificationsProvider';
import { IntlProvider } from './common/intl/IntlContext';
import { LazyImageProvider } from './common/LazyImageContext';
import { GlobalStyle, ThemeProvider } from '@core-vehicle/core';

const TranslateProvider = ({ children }) => (
  <TranslateContext.Provider value={translate}>
    {children}
  </TranslateContext.Provider>
);

const IntlPolyfill = ({ children }) => {
  const supportsIntl = !!window.Intl;
  const [ready, setReady] = useState(supportsIntl);

  useEffect(() => {
    if (!supportsIntl) {
      const script = document.createElement('script');
      const { language, country } = window.appResources;
      script.async = false;
      script.src =
        'https://cdn.polyfill.io/v2/polyfill.min.js?features=Intl.~locale.' +
        language +
        '-' +
        country;
      script.onload = () => {
        setReady(true);
      };
      document.head.appendChild(script);
    }
  }, []);

  return ready ? children : null;
};

const Providers = ({ assetsBrand, language, country, children }) => {
  return (
    <IntlPolyfill>
      <BrowserRouter>
        <ReduxProvider store={window.__store__}>
          <ModelInfoProvider>
            <ThemeContext.Provider value={assetsBrand}>
              <SvgIconProvider brand={assetsBrand}>
                <TranslateProvider>
                  <NotificationsProvider disabled={false}>
                    <IntlProvider language={language} country={country}>
                      <LazyImageProvider>{children}</LazyImageProvider>
                    </IntlProvider>
                  </NotificationsProvider>
                </TranslateProvider>
              </SvgIconProvider>
            </ThemeContext.Provider>
          </ModelInfoProvider>
        </ReduxProvider>
      </BrowserRouter>
    </IntlPolyfill>
  );
};

const AppProvider = ({ children }) => {
  const { country, language, assetsBrand } = window.appResources;
  const coreBrands = ['cadillac', 'fleet'];

  return (
    <>
      {assetsBrand && coreBrands.includes(assetsBrand) ? (
        <ThemeProvider theme={assetsBrand}>
          <GlobalStyle />
          <Providers
            country={country}
            language={language}
            assetsBrand={assetsBrand}>
            {children}
          </Providers>
        </ThemeProvider>
      ) : (
        <Providers
          country={country}
          language={language}
          assetsBrand={assetsBrand}>
          {children}
        </Providers>
      )}
    </>
  );
};

export default AppProvider;
